// ============================================================
// sheetChanges - what a live sheet says differently from the library
// sheet it came from.
//
// Sheet edits are not undoable (undo restores combat state only), so
// this list is how a mistake is seen and reversed: every difference,
// with the value it had, and a reset that puts that one value back.
//
// Weapons are not listed: the armoury owns them and has its own resets.
// Pure.
// ============================================================
import { effectSentence, readEffects } from '../character/effectItems';
import { readAlwaysOn, readSoloModes } from '../character/soloModes';
import type { Character, SkillId, SkillRating, TalentInstance, TraitInstance } from '../character/types';
import { CHARACTERISTICS, PACES } from '../character/sheetPatches';
import { TIER_LABELS, tierLevel } from '../character/skillTiers';
import { SKILL_MAP } from '../character/skillCatalog';
import { talentText, traitText } from '../character/instanceText';
import { sameValue } from '../canonicalJson';
import { withPath } from '../targetEdits';
import { armourKeyName } from './pins';

/** One difference between the live sheet and its library original. */
export interface SheetChange {
  /** Dotted path into the Character: 'characteristics.tgh.value', 'talents'. */
  path: string;
  label: string;
  /** The library's value and the live one, as short text. */
  from: string;
  to: string;
}

type Bag = Record<string, unknown>;

const at = (sheet: Character, path: string): unknown =>
  path.split('.').reduce<unknown>((node, key) => (node as Bag | undefined)?.[key], sheet);

/** A skill rating as words: "+10", or "+10, mod -5". */
function ratingText(value: unknown): string {
  const rating = { trained: false, plus10: false, plus20: false, mod: 0, ...((value ?? {}) as Partial<SkillRating>) };
  const tier = TIER_LABELS[tierLevel(rating)].toLowerCase();
  return rating.mod ? `${tier}, mod ${rating.mod}` : tier;
}

const named = (name: string, parameter?: string) => (parameter ? `${name} (${parameter})` : name);

/** The sheet's talents as names: "Hatred (Orks), Swift Attack". */
const talentsText = (value: unknown): string =>
  ((value ?? []) as TalentInstance[]).map((t) => named(talentText(t)?.name ?? t.talentId, t.parameter)).join(', ') ||
  'none';

/** The sheet's traits as names. */
const traitsText = (value: unknown): string =>
  ((value ?? []) as TraitInstance[]).map((t) => named(traitText(t)?.name ?? t.traitId, t.parameter)).join(', ') ||
  'none';

/** A list of named bundles as names: "Assault, Bulwark". */
const namesText =
  (read: (raw: unknown) => { name: string }[]) =>
  (value: unknown): string =>
    read(value)
      .map((b) => b.name)
      .join(', ') || 'none';

/** The always-on effects as sentences. */
const effectsText = (value: unknown): string => readEffects(value).map(effectSentence).join(', ') || 'none';

/** A flat value as short text: on / off for a switch, "none" for nothing. */
export const plain = (value: unknown): string => {
  if (typeof value === 'boolean') return value ? 'on' : 'off';
  return value === undefined || value === null || value === '' ? 'none' : String(value);
};

interface Field {
  path: string;
  label: string;
  text?: (value: unknown) => string;
}

/** The fields that are always worth comparing. */
const FIXED: Field[] = [
  ...CHARACTERISTICS.flatMap(({ id, name }): Field[] => [
    { path: `characteristics.${id}.value`, label: name },
    { path: `characteristics.${id}.unnaturalMultiplier`, label: `Unnatural ${name}` },
  ]),
  { path: 'wounds.max', label: 'Wounds max' },
  { path: 'horde.maxMagnitude', label: 'Magnitude max' },
  { path: 'psyRating', label: 'Psy rating' },
  { path: 'fearRating', label: 'Fear' },
  { path: 'armour.name', label: 'Armour' },
  { path: 'bodyType', label: 'Body' },
  { path: 'horde.isHorde', label: 'Horde' },
  { path: 'forceField.active', label: 'Force field' },
  { path: 'forceField.protectionRating', label: 'Force field rating' },
  { path: 'forceField.overloadThreshold', label: 'Force field overload' },
  ...PACES.map((pace): Field => ({ path: `movement.${pace}`, label: `Move (${pace})` })),
  { path: 'size', label: 'Size' },
  { path: 'tokenDiameter', label: 'Token size' },
  { path: 'vehicleKind', label: 'Vehicle kind' },
  { path: 'figure', label: 'Figure' },
  { path: 'xenos', label: 'Xenos' },
  { path: 'traits', label: 'Traits', text: traitsText },
  { path: 'talents', label: 'Talents', text: talentsText },
  { path: 'alwaysOn', label: 'Always on items', text: namesText(readAlwaysOn) },
  { path: 'soloModes', label: 'Solo modes', text: namesText(readSoloModes) },
  { path: 'permanentEffects', label: 'Always on', text: effectsText },
];

const keysOf = (...bags: unknown[]) => [...new Set(bags.flatMap((bag) => Object.keys((bag ?? {}) as Bag)))];

/** The fields that depend on what the two sheets hold: each armour location, each rated skill. */
function keyedFields(live: Character, base: Character): Field[] {
  const locations = keysOf(live.armour?.locations, base.armour?.locations).map((loc): Field => ({
    path: `armour.locations.${loc}`,
    label: `Armour: ${armourKeyName(loc)}`,
  }));
  const skills = keysOf(live.skills, base.skills).map((id): Field => ({
    path: `skills.${id}`,
    label: SKILL_MAP[id as SkillId]?.name ?? id,

    text: ratingText,
  }));
  return [...locations, ...skills];
}

/** A skill rating compared by what it MEANS: the deprecated mirror and an absent rating are not changes. */
function comparable(field: Field, value: unknown): unknown {
  if (!field.path.startsWith('skills.')) return value ?? null;
  const { trained = false, plus10 = false, plus20 = false, mod = 0 } = (value ?? {}) as Partial<SkillRating>;
  return { trained, plus10, plus20, mod };
}

/**
 * Every difference between the live sheet and the library sheet it came
 * from, in sheet order. Empty with no library sheet to compare against
 * (a plain statblock has no original).
 */
export function sheetChanges(live: Character, base: Character | null): SheetChange[] {
  if (!base) return [];
  return [...FIXED, ...keyedFields(live, base)].flatMap((field) => {
    const [was, now] = [at(base, field.path), at(live, field.path)];
    if (sameValue(comparable(field, was), comparable(field, now))) return [];
    const text = field.text ?? plain;
    return [{ path: field.path, label: field.label, from: text(was), to: text(now) }];
  });
}

/** The paths that say what KIND of body it is; they are reset together, with the armour that goes with the body. */
const BODY_PATHS: ReadonlySet<string> = new Set(['bodyType', 'horde.isHorde']);

/** `sheet` with the leaf at `path` removed (the library never had it). */
function withoutPath(sheet: Character, path: string): Character {
  const keys = path.split('.');
  const next = withPath(sheet, path, undefined);
  const parent = keys.slice(0, -1).reduce<Bag>((node, key) => node[key] as Bag, next as unknown as Bag);
  delete parent[keys[keys.length - 1]];
  return next;
}

/**
 * The sheet's partial that puts one changed value back to the library's.
 *
 * The body is put back WHOLE - its type, its crowd flag and the armour
 * that belongs to it - because a body type on its own leaves the armour
 * keyed for the body it was switched to. Use `isBodyChange` to give the
 * live state the same treatment.
 */
export function resetChange(live: Character, base: Character, path: string): Partial<Character> {
  if (BODY_PATHS.has(path)) {
    return { bodyType: base.bodyType, horde: { ...live.horde, isHorde: !!base.horde?.isHorde }, armour: base.armour };
  }
  const top = path.split('.')[0] as keyof Character;
  const value = at(base, path);
  const next = value === undefined ? withoutPath(live, path) : withPath(live, path, value);
  return { [top]: next[top] };
}

/** Is this change one of the body's (so its reset must re-body the live state too)? */
export function isBodyChange(path: string): boolean {
  return BODY_PATHS.has(path);
}
